import { errorMessage } from "@api/errors";
import { useGenealogyEnvironments } from "@api/generated/genealogy/genealogy";
import { useMeasurementsVisualTraits } from "@api/generated/measurements/measurements";
import type { EnvironmentValue } from "@api/generated/model";
import { Link } from "@tanstack/react-router";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChartFrame, valueText } from "./Charts";

export function EnvironmentPerformance({ paz, sid }: { paz: number; sid: number }) {
  const { t: tr } = useTranslation();
  const [all, setAll] = useState(false); // environments without a value are counted, listed only when asked
  const traits = useMeasurementsVisualTraits().data ?? [];
  const fields = traits.map((t) => t.id);
  const query = useGenealogyEnvironments(paz, sid, { fields }, { query: { enabled: !!fields.length } });
  return (
    <section className="card pad">
      <h2 className="h2">{tr("visual.performance")}</h2>
      <p className="sm mu">{tr("visual.performanceHelp")}</p>
      {query.isFetching && <p role="status">{tr("visual.refreshing")}</p>}
      {query.error && <p role="alert">{errorMessage(query.error)}</p>}
      {traits.map((t) => {
        const every = (query.data ?? []).filter((r) => r.field === t.id);
        const measured = every.filter((r) => r.value != null);
        const unmeasured = every.length - measured.length;
        return (
          <ChartFrame key={t.id} title={`${t.label} · ${t.unit ?? tr("visual.scaleUnverified")}`} scope={tr("visual.environmentLegend")}>
            <EnvironmentTable rows={all ? every : measured} paz={paz} sid={sid} />
            {unmeasured > 0 && (
              <button type="button" className="link-button sm" onClick={() => setAll(!all)}>
                {all ? tr("visual.measuredOnly") : tr("visual.unmeasuredCount", { count: unmeasured })}
              </button>
            )}
          </ChartFrame>
        );
      })}
    </section>
  );
}

/** One property's environments: the mean there drawn against its reference (the standards' or the trial's mean).
 *  Every property's table shares one saved sort, filter and column order. */
function EnvironmentTable({ rows, paz, sid }: { rows: EnvironmentValue[]; paz: number; sid: number }) {
  const { t } = useTranslation();
  const columns = useMemo<Column<EnvironmentValue>[]>(() => {
    const values = rows.flatMap((r) => [r.value, r.reference]).filter((v): v is number => v != null);
    const lo = Math.min(0, ...values);
    const hi = Math.max(0, ...values);
    // across the strip, as a share of its width (4–96 %), so the dot stays round at any column width
    const at = (v: number) => `${4 + 92 * (hi === lo ? 0.5 : (v - lo) / (hi - lo))}%`;
    const reference = (r: EnvironmentValue) =>
      `${r.reference_basis === "standards" ? t("visual.standardMean") : t("visual.trialMean")}: ${valueText(r.reference)} (${r.reference_count})`;
    return [
      {
        id: "environment",
        header: t("visual.environment"),
        value: (r) => `${r.ev} · ${r.kis} · ${r.th}`,
        cell: (r) => (
          <Link to="/trials/data" search={{ ev: r.ev, kis: r.kis, th: r.th, table: r.field.split(".")[0], paz, sid, alp: 0 }}>
            {r.ev} · {r.kis} · {r.th}
          </Link>
        ),
      },
      {
        id: "mean",
        header: t("visual.observedMean"),
        value: (r) => r.value,
        text: (r) => valueText(r.value),
        cell: (r) => (
          // the value and its strip side by side: the strip takes what the column leaves (also a narrowed one)
          <span className="env-mean">
            <span>{valueText(r.value)}</span>
            <svg height="22" aria-hidden="true">
              {r.reference != null && <line x1={at(r.reference)} x2={at(r.reference)} y1="2" y2="20" stroke="currentColor" />}
              {r.value != null && <circle cx={at(r.value)} cy="11" r="5" fill="var(--b-accent, #328777)" />}
            </svg>
          </span>
        ),
      },
      { id: "plots", header: "n", name: t("visual.plots"), value: (r) => r.plots, numeric: true },
      { id: "reference", header: t("visual.referenceN"), value: (r) => r.reference, text: reference },
    ];
  }, [rows, paz, sid, t]);
  const table = useDataTable("genealogy.environments", columns, rows);
  return (
    <DataTable table={table} rowKey={(r) => `${r.ev}/${r.kis}/${r.th}`} className="visual-scroll" empty={t("visual.environmentEmpty")} />
  );
}
